/**
 * "Add to beat / brief" — pick a campaign, then a beat (default, recommended), the whole campaign or a specific
 * collaboration (creator), an optional note, and POST /attachments. One track per target: this replaces the target's
 * previous pick. The track then shows on the brief (Soundtrack block); the most specific pick wins.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Music2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/components/ui/utils';
import { audioStudioApi, type AttachmentTargetType } from '@/api/audioStudioApi';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useCampaign } from '@/hooks/useCampaign';
import { useCampaignCollaborations } from '@/hooks/useCampaignCollaborations';
import { buildAttachmentPayload, type AttachableTrack } from './attachPayload';
import { audioKeys } from './queries';

const selectCls = 'w-full rounded-lg border border-[#d0d5dd] bg-white px-3 py-2 text-[13px] text-[#344054] focus:border-[#be43ff] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20';

export function AttachToCampaignDialog({ track, open, onOpenChange }: { track: AttachableTrack | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [targetType, setTargetType] = useState<AttachmentTargetType>('beat');
  const [targetId, setTargetId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => { const t = setTimeout(() => setDebounced(text.trim()), 300); return () => clearTimeout(t); }, [text]);
  useEffect(() => { if (!open) { setCampaignId(''); setTargetId(''); setNote(''); setTargetType('beat'); setText(''); } }, [open]);
  useEffect(() => { setTargetId(''); }, [campaignId, targetType]);

  const campaigns = useCampaigns({ teamScope: user?.team?.team_id || '', pageSize: 50, filters: debounced ? { text: debounced } : {} });
  const campaign = useCampaign({ campaignId: campaignId || undefined, enabled: open && !!campaignId });
  const collabs = useCampaignCollaborations({ campaignId: campaignId || undefined, enabled: open && !!campaignId && targetType === 'collaboration' });

  const beats = useMemo(() => (campaign.data?.beats || []).filter((b) => b.beat_id), [campaign.data?.beats]);
  const collaborations = collabs.data?.collaborationsClientAccepted || [];

  // One beat → preselect it (the common case).
  useEffect(() => { if (targetType === 'beat' && beats.length === 1 && !targetId) setTargetId(beats[0].beat_id); }, [beats, targetType, targetId]);

  const payload = track ? buildAttachmentPayload(track, { campaignId, targetType, targetId, note }) : null;
  const attach = useMutation({
    mutationFn: () => audioStudioApi.createAttachment(payload!),
    onSuccess: () => {
      toast.success(targetType === 'campaign' ? 'Track set for the whole campaign' : targetType === 'beat' ? 'Track set for the beat' : 'Track set for the collaboration');
      void qc.invalidateQueries({ queryKey: audioKeys.attachmentsAll });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message || 'Could not attach the sound'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to beat / brief</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Music2 className="h-3.5 w-3.5 text-[#be43ff]" aria-hidden /> <span className="truncate">{track?.title}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[12px] text-[#344054]" style={{ fontWeight: 600 }} htmlFor="attach-campaign">Campaign</label>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" aria-hidden />
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search campaigns…" aria-label="Search campaigns" className={cn(selectCls, 'pl-8')} />
            </div>
            <select id="attach-campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={selectCls}>
              <option value="">{campaigns.isLoading ? 'Loading campaigns…' : campaigns.data.campaigns.length ? 'Pick a campaign' : 'No campaigns found'}</option>
              {campaigns.data.campaigns.map((c) => (
                <option key={c.campaign_id} value={c.campaign_id}>{c.name}{c.brand_name ? ` — ${c.brand_name}` : ''}</option>
              ))}
            </select>
          </div>

          {campaignId && (
            <div>
              <div className="mb-1.5 text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>Attach to</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Attach to">
                {([
                  ['beat', 'A beat', 'Every creator on the beat gets it (recommended)'],
                  ['campaign', 'Whole campaign', 'Every beat without its own track'],
                  ['collaboration', 'A creator', 'Only one collaboration'],
                ] as const).map(([id, label, hint]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={targetType === id}
                    onClick={() => setTargetType(id)}
                    className={cn('rounded-lg border px-3 py-2 text-left', targetType === id ? 'border-[#be43ff] bg-[#faf5ff]' : 'border-[#d0d5dd] hover:bg-[#f9fafb]')}
                  >
                    <span className="block text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{label}</span>
                    <span className="block text-[11px] text-[#667085]">{hint}</span>
                  </button>
                ))}
              </div>

              {/* The whole campaign needs no further pick: the campaign is the target. */}
              {targetType !== 'campaign' && <div className="mt-3">
                {targetType === 'beat' ? (
                  <select aria-label="Beat" value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selectCls}>
                    <option value="">{campaign.isLoading ? 'Loading beats…' : beats.length ? 'Pick a beat' : 'This campaign has no beats yet'}</option>
                    {beats.map((b) => <option key={b.beat_id} value={b.beat_id}>{b.beat_name || b.beat_id}</option>)}
                  </select>
                ) : (
                  <select aria-label="Collaboration" value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selectCls}>
                    <option value="">{collabs.isLoading ? 'Loading creators…' : collaborations.length ? 'Pick a creator' : 'No collaborations yet'}</option>
                    {collaborations.map((c) => (
                      <option key={c.collaboration_id} value={c.collaboration_id}>
                        {(c.influencer?.name || '').trim() || `@${c.influencer?.handle || c.collaboration_id}`}{c.influencer?.handle ? ` (@${c.influencer.handle})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>}
            </div>
          )}

          <div>
            <label htmlFor="attach-note" className="mb-1.5 block text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>Note for the creators <span className="font-normal text-[#98a2b3]">(optional)</span></label>
            <Textarea id="attach-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="e.g. Use the first 15 seconds under the product reveal" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!payload || attach.isPending} onClick={() => attach.mutate()} className="bg-[#be43ff] text-white hover:bg-[#a835e0]">
            {attach.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
